import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdContentLanguageService } from '../hd-content-language.service';

/** Doel van de hernoem-dialog (tab-contextmenu); null = gesloten. */
export interface HdChartRenameTarget {
  currentName: string;
}

/** Klein dialoogje om puur de chartnaam (= tabtitel) te hernoemen. */
@Component({
  selector: 'hd-chart-rename-dialog',
  standalone: true,
  imports: [SharedModule, FormsModule],
  templateUrl: './hd-chart-rename-dialog.component.html',
  styleUrl: './hd-chart-rename-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartRenameDialogComponent {
  readonly target = model<HdChartRenameTarget | null>(null);
  readonly confirmed = output<string>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly name = signal('');

  constructor() {
    effect(() => {
      const target = this.target();
      if (target != null) {
        this.name.set(target.currentName);
      }
    });
  }

  protected readonly text = computed(() => {
    const lang = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, lang);
    return {
      title: translate('humanDesign.chart.controls.renameChart'),
      placeholder: translate('humanDesign.chart.controls.renamePlaceholder'),
      save: translate('humanDesign.chart.controls.renameSave'),
    };
  });

  protected get canSave(): boolean {
    return this.name().trim().length > 0;
  }

  protected onOpenChange(open: boolean): void {
    if (!open) {
      this.target.set(null);
    }
  }

  protected submit(): void {
    const newName = this.name().trim();
    if (!newName) {
      return;
    }
    this.confirmed.emit(newName);
    this.target.set(null);
  }
}
